import type { DateRange } from '#core/types/date-filter'
import type { LiquidityDepth } from '@forgd/contract'
import { formatValue } from '@forgd/formatter'

export const TABLE_PAGE_SIZE = 7
export const MMM_DEMO_DASHBOARD_PATH = '/market-maker-monitoring/dashboard'

export function getYTicks(data: (number | null)[], targetTickCount = 4, paddingPercentage = 0.2) {
  if (!data || data.length === 0) {
    return [0, 1] // Simple default if no data
  }

  const values = data.filter(v => typeof v === 'number') // filter out null values
  const minVal = Math.min(...values)
  const maxVal = Math.max(...values)

  // Handle case where all values are the same
  if (minVal === maxVal) {
    // Provide a fixed percentage range (e.g., 20%) or a small absolute range if value is 0
    const padding = Math.max(Math.abs(minVal * paddingPercentage), 1e-6)
    return [minVal - padding, minVal, minVal + padding].sort((a, b) => a - b)
  }

  const range = maxVal - minVal

  // Add padding *before* calculating step size for a more stable range
  const paddingAmount = range * paddingPercentage
  const paddedMin = minVal - paddingAmount
  const paddedMax = maxVal + paddingAmount
  const paddedRange = paddedMax - paddedMin

  // Calculate a "nice" step size based on the padded range
  const roughStep = paddedRange / (targetTickCount - 1)
  const magnitude = 10 ** Math.floor(Math.log10(roughStep))
  const normalizedStep = roughStep / magnitude

  let niceStep
  if (normalizedStep <= 1) {
    niceStep = magnitude
  }
  else if (normalizedStep <= 2) {
    niceStep = 2 * magnitude
  }
  else if (normalizedStep <= 5) {
    niceStep = 5 * magnitude
  }
  else {
    niceStep = 10 * magnitude
  }

  // Calculate tick range based on nice step and *padded* min/max
  const tickMin = Math.floor(paddedMin / niceStep) * niceStep
  const tickMax = Math.ceil(paddedMax / niceStep) * niceStep

  // Generate ticks
  const ticks: number[] = []
  let currentTick = tickMin
  const epsilon = niceStep * 1e-9

  while (currentTick <= tickMax + epsilon) {
    ticks.push(Number.parseFloat(currentTick.toPrecision(12))) // Limit precision
    currentTick += niceStep
    if (ticks.length > 15) {
      break // Safety break
    }
  }

  // Ensure at least two ticks, using padded values if necessary
  if (ticks.length < 2) {
    // Use the initially calculated padded min/max for fallback
    return [paddedMin, paddedMax].sort((a, b) => a - b)
  }

  return ticks
}

export function requireMmmProjectId(projectId: string | null | undefined): string {
  return projectId ?? ''
}

export const depthLabels: Record<LiquidityDepth, string> = {
  50: '(+/-) 0.50%',
  100: '(+/-) 1.00%',
  200: '(+/-) 2.00%',
}

export function getDateAvgLabel(dateRange: DateRange) {
  if (dateRange.timeframe === '24h') {
    return 'Average: 24h'
  }
  if (dateRange.timeframe === 'custom') {
    return `Average: ${formatValue(dateRange.start, 'date')}`
  }
  return `Average: ${formatValue(dateRange.start, 'date')} - ${formatValue(dateRange.end, 'date')}`
}
